import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ActionIcon, Badge, Box, Group, Paper, Text } from "@mantine/core";
import React, { useState } from "react";
import { RemovableChip } from "../../../components/common/RemovableChip";

export interface ExpandedCriteriaChipProps {
    label: string;
    items: { id: number; label: string }[];
    onRemoveItem: (id: number) => void;
    onRemoveAll: () => void;
}

/**
 * A collapsible group chip for filter criteria that expand to many underlying Technology IDs
 * (Canonical, Position Profile). Collapsed: shows the group name + count as a compact badge.
 * Expanded: renders individual RemovableChip items so the user can prune specific technologies
 * out of the group without having to remove the whole group and re-add it.
 */
export const ExpandedCriteriaChip: React.FC<ExpandedCriteriaChipProps> = ({
    label,
    items,
    onRemoveItem,
    onRemoveAll,
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!expanded) {
        return (
            <Badge
                size="md"
                variant="light"
                color="sopraPurple"
                pl={10}
                pr={4}
                style={{ cursor: "default" }}
                rightSection={
                    <Group gap={6} wrap="nowrap" align="center">
                        <Text size="sm" c="sopraPurple.4" fw={500} mr={2}>
                            {items.length}
                        </Text>
                        <ActionIcon
                            size={22}
                            variant="transparent"
                            color="sopraPurple"
                            onClick={() => setExpanded(true)}
                            aria-label={`Expand ${label}`}
                            style={{ pointerEvents: "all" }}
                        >
                            <ChevronDownIcon style={{ width: 15, height: 15 }} />
                        </ActionIcon>
                        <ActionIcon
                            size={22}
                            variant="transparent"
                            color="sopraPurple"
                            onClick={onRemoveAll}
                            aria-label={`Remove ${label}`}
                            style={{ pointerEvents: "all" }}
                        >
                            <XMarkIcon style={{ width: 14, height: 14 }} />
                        </ActionIcon>
                    </Group>
                }
            >
                {label}
            </Badge>
        );
    }

    return (
        <Box w="100%">
            <Paper withBorder shadow="xs" radius="md" p="sm">
                <Group justify="space-between" align="center" mb="sm" wrap="nowrap">
                    <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
                        <Text size="sm" fw={700} tt="uppercase" c="sopraPurple.5" style={{ letterSpacing: "0.05em" }}>
                            {label}
                        </Text>
                        <Badge size="sm" variant="light" color="sopraPurple">
                            {items.length}
                        </Badge>
                    </Group>
                    <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
                        <ActionIcon
                            size="md"
                            variant="subtle"
                            color="sopraPurple"
                            onClick={() => setExpanded(false)}
                            aria-label="Collapse"
                        >
                            <ChevronUpIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                        <ActionIcon
                            size="md"
                            variant="subtle"
                            color="red"
                            onClick={onRemoveAll}
                            aria-label={`Remove all from ${label}`}
                        >
                            <XMarkIcon style={{ width: 16, height: 16 }} />
                        </ActionIcon>
                    </Group>
                </Group>
                <Group gap="xs" wrap="wrap">
                    {items.map((item) => (
                        <RemovableChip
                            key={item.id}
                            label={item.label}
                            onRemove={() => onRemoveItem(item.id)}
                        />
                    ))}
                </Group>
            </Paper>
        </Box>
    );
};
